import React, { useEffect, useRef } from 'react';
import '../Style/Abouthero.css';
import { useLanguage } from "../Context/Languagecontext";

const AboutHero = () => {
  const heroRef = useRef(null);
  const { t, lang } = useLanguage();
  const heroData = t.aboutHero;

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('in-view');
        }
      },
      { threshold: 0.1 }
    );
    if (heroRef.current) observer.observe(heroRef.current);
    return () => observer.disconnect();
  }, []);

  // Safe fallback processing for configuration row maps
  const statsList = heroData?.stats || [
    { num: '3+',   label: 'Decades of Excellence' },
    { num: '12',   label: 'Industry Accolades'    },
    { num: '50K+', label: 'Lives Transformed'     },
  ];

  return (
    <section 
      className={`about-hero${lang === 'ta' ? ' about-hero--tamil' : ''}`} 
      ref={heroRef}
    >
      {/* ── Ambient background layers ── */}
      <div className="ah-bg-layer ah-bg-vignette" />
      <div className="ah-bg-layer ah-bg-grain" />
      <div className="ah-bg-layer ah-bg-radial" />

      {/* ── Decorative corner ornaments ── */}
      <svg className="ah-ornament ah-ornament--tl" viewBox="0 0 120 120" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M10 110 Q10 10 110 10" stroke="var(--clr-gold)" strokeWidth="0.8" fill="none" opacity="0.5"/>
        <path d="M10 90  Q10 10  90 10" stroke="var(--clr-gold)" strokeWidth="0.4" fill="none" opacity="0.3"/>
        <circle cx="10" cy="10" r="3" fill="var(--clr-gold)" opacity="0.6"/>
        <circle cx="110" cy="10" r="1.5" fill="var(--clr-gold)" opacity="0.4"/>
        <circle cx="10" cy="110" r="1.5" fill="var(--clr-gold)" opacity="0.4"/>
      </svg>
      <svg className="ah-ornament ah-ornament--tr" viewBox="0 0 120 120" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M110 110 Q110 10 10 10" stroke="var(--clr-gold)" strokeWidth="0.8" fill="none" opacity="0.5"/>
        <path d="M110 90  Q110 10 30 10" stroke="var(--clr-gold)" strokeWidth="0.4" fill="none" opacity="0.3"/>
        <circle cx="110" cy="10" r="3" fill="var(--clr-gold)" opacity="0.6"/>
      </svg>

      {/* ── Floating gold particles ── */}
      {[...Array(12)].map((_, i) => (
        <span key={i} className={`ah-particle ah-particle--${i + 1}`} />
      ))}

      {/* ── Hero Content ── */}
      <div className="ah-content">

        {/* Eyebrow label */}
        <div className="ah-eyebrow">
          <span className="ah-eyebrow__line" />
          <span className="ah-eyebrow__text">{heroData?.eyebrow}</span>
          <span className="ah-eyebrow__line" />
        </div>

        {/* Main headline */}
        <h1 className="ah-title">
          <span className="ah-title__row">
            <span className="ah-word ah-word--1">{heroData?.titleRow1?.w1}</span>
            <span className="ah-word ah-word--2">{heroData?.titleRow1?.w2}</span>
            <span className="ah-word ah-word--serif ah-word--3">{heroData?.titleRow1?.w3}</span>
          </span>
          <span className="ah-title__row">
            <span className="ah-word ah-word--4">{heroData?.titleRow2?.w4}</span>
            <span className="ah-word ah-word--5">{heroData?.titleRow2?.w5}</span>
            <span className="ah-word ah-word--serif ah-word--6">{heroData?.titleRow2?.w6}</span>
          </span>
        </h1>

        {/* Sub-copy */}
        <p className="ah-subtitle">{heroData?.subtitle}</p>

        {/* Stats — single horizontal row with dividers */}
        <div className="ah-stats">
          {statsList.map(({ num, label }, i) => (
            <React.Fragment key={label}>
              {i > 0 && <span className="ah-stat-divider" />}
              <div className="ah-stat" style={{ '--stat-delay': `${1.0 + i * 0.15}s` }}>
                <span className="ah-stat__num">{num}</span>
                <span className="ah-stat__label">{label}</span>
              </div>
            </React.Fragment>
          ))}
        </div>
      </div>

      {/* ── Bottom wave curve ── */}
      <div className="ah-wave-wrap">
        <svg
          className="ah-wave"
          viewBox="0 0 1440 110"
          preserveAspectRatio="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M0,60 C240,110 480,10 720,55 C960,100 1200,20 1440,60 L1440,110 L0,110 Z"
            fill="rgba(54,4,5,0.18)"
          />
          <path
            d="M0,65 C240,115 480,15 720,60 C960,105 1200,25 1440,65 L1440,110 L0,110 Z"
            fill="var(--clr-ivory)"
          />
          <path
            d="M0,65 C240,115 480,15 720,60 C960,105 1200,25 1440,65"
            fill="none"
            stroke="var(--clr-gold)"
            strokeWidth="1.2"
            opacity="0.55"
          />
        </svg>
      </div>

    </section>
  );
};

export default AboutHero;